import { ComponentType, FC, Suspense, lazy, useContext, useEffect } from 'react';
import DefaultLayout from './layouts/DefaultLayout';
import { PageTitleContext } from './layouts/pageTitleContext';
import AuthLayout from './layouts/auth/AuthLayout';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import PrivateRoute from './PrivateRoute';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { RealtimeProvider } from './contexts/RealtimeContext';
import RealtimePanicOverlay from './components/RealtimePanicOverlay';
import RealtimeIncidentOverlay from './components/RealtimeIncidentOverlay';
import BuddyOverdueBanner from './components/BuddyOverdueBanner';
import Login from './pages/auth/login';
import PublicOnlyRoute from './PublicOnlyRoute';
import VerifyLoginForm from './pages/auth/VerifyLoginForm';
import ForgotPassword from './pages/auth/forgotPassword';
import RedeemInvite from './pages/auth/RedeemInvite';
import { PageName, PagesPermissions } from './types/Authorization';
import { RouteErrorBoundary } from './components/ErrorBoundary';

/** Old singular links (search results, bookmarks, emails) → the real routes. */
const RedirectWithId: FC<{ to: string }> = ({ to }) => {
  const { id } = useParams();
  return <Navigate to={`${to}${id ?? ""}`} replace />;
};

// ---------------------------------------------------------------------------
// Route-level code splitting (2026-08-09). Every page behind authentication is
// loaded on demand via React.lazy so the main bundle only carries the shell
// (layouts, auth pages, providers). Layouts, PrivateRoute and the login flow
// stay static on purpose — they are needed on first paint.
// ---------------------------------------------------------------------------
const AccessDenied = lazy(() => import('./pages/AccessDenied'));
const Dashboard = lazy(() => import('./pages/dashboard/dashboard'));
const RiskPage = lazy(() => import('./pages/mission/risk/RiskPage'));
const ReportsPage = lazy(() => import('./pages/reports/Reports'));
const Role = lazy(() => import('./pages/organization/role/Role'));
const RoleList = lazy(() => import('./pages/organization/role/RolesList'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Contact = lazy(() => import('./pages/contacts/contact'));
const ContactsList = lazy(() => import('./pages/contacts/ContactsList'));
const Provider = lazy(() => import('./pages/provider/Provider'));
const ProviderList = lazy(() => import('./pages/provider/ProvidersList'));
const ClientList = lazy(() => import('./pages/client/ClientsList'));
const Client = lazy(() => import('./pages/client/Client'));
const ServiceList = lazy(() => import('./pages/lookups/service/ServicesList'));
const ServiceForm = lazy(() => import('./pages/lookups/service/Service'));
const SubServiceForm = lazy(() => import('./pages/lookups/service/SubServiceForm'));
const TagForm = lazy(() => import('./pages/lookups/tags/Tag'));
const TagList = lazy(() => import('./pages/lookups/tags/TagsList'));
const ManufacturerForm = lazy(() => import('./pages/lookups/manufacturer/Manufacturer'));
const ManufacturerList = lazy(() => import('./pages/lookups/manufacturer/ManufacturersList'));
const ExpenseForm = lazy(() => import('./pages/lookups/expense/Expense'));
const ExpenseList = lazy(() => import('./pages/lookups/expense/ExpenseList'));
const IncidentForm = lazy(() => import('./pages/lookups/incident/Incident'));
const IncidentList = lazy(() => import('./pages/lookups/incident/IncidentList'));
const ColorForm = lazy(() => import('./pages/lookups/color/Color'));
const ColorList = lazy(() => import('./pages/lookups/color/ColorsList'));
const RegionForm = lazy(() => import('./pages/lookups/region/Region'));
const RegionList = lazy(() => import('./pages/lookups/region/RegionsList'));
const CountryForm = lazy(() => import('./pages/lookups/country/Country'));
const CountryList = lazy(() => import('./pages/lookups/country/CountriesList'));
const StateForm = lazy(() => import('./pages/lookups/state/State'));
const StateList = lazy(() => import('./pages/lookups/state/StatesList'));
const CityForm = lazy(() => import('./pages/lookups/city/City'));
const CityList = lazy(() => import('./pages/lookups/city/CitiesList'));
const TimeZoneForm = lazy(() => import('./pages/lookups/timeZone/TimeZone'));
const TimeZoneList = lazy(() => import('./pages/lookups/timeZone/TimeZonesList'));
const AirportList = lazy(() => import('./pages/lookups/airport/AirportsList'));
const AirportForm = lazy(() => import('./pages/lookups/airport/Airport'));
const OrganizationDetails = lazy(() => import('./pages/organization/organization/Organization'));
const TeamList = lazy(() => import('./pages/organization/team/TeamsList'));
const Team = lazy(() => import('./pages/organization/team/Team'));
const JobPositionForm = lazy(() => import('./pages/lookups/jobPosition/JobPosition'));
const JobPositionsList = lazy(() => import('./pages/lookups/jobPosition/JobPositionsList'));
const UserList = lazy(() => import('./pages/organization/user/UsersList'));
const UserDetails = lazy(() => import('./pages/organization/user/User'));
const OrganizationSpecialtyForm = lazy(() => import('./pages/lookups/specialty/Specialty'));
const OrganizationSpecialtyList = lazy(() => import('./pages/lookups/specialty/specialitiesList'));
const SearchAliasesList = lazy(() => import('./pages/lookups/searchAlias/SearchAliasesList'));
const SearchAliasForm = lazy(() => import('./pages/lookups/searchAlias/SearchAlias'));
const SearchLog = lazy(() => import('./pages/system/SearchLog'));
const ContinentForm = lazy(() => import('./pages/lookups/continent/Continent'));
const ContinentList = lazy(() => import('./pages/lookups/continent/ContinentsList'));
const CompanyForm = lazy(() => import('./pages/company/Company'));
const CompaniesList = lazy(() => import('./pages/company/CompaniesList'));
const MergeContactsForm = lazy(() => import('./pages/contacts/Merge'));
const MergeOrganizationsForm = lazy(() => import('./components/organization/Merge'));
const LanguageForm = lazy(() => import('./pages/lookups/language/Language'));
const LanguageList = lazy(() => import('./pages/lookups/language/LanguagesList'));
const MissionRequestDetails = lazy(() => import('./pages/missionRequest/MissionRequest'));
const MissionRequestsList = lazy(() => import('./pages/missionRequest/MissionRequestsList'));
const MissionsList = lazy(() => import('./pages/mission/MissionsList'));
const OpsBoard = lazy(() => import('./pages/mission/OpsBoard'));
const ProceduresPage = lazy(() => import('./pages/procedures/ProceduresPage'));
const MissionDetails = lazy(() => import('./pages/mission/Mission'));
// PUBLIC Live Mission Link page — no auth, no layout; the token in the URL is
// the credential. Kept lazy so anonymous visitors only download this chunk.
const TrackMission = lazy(() => import('./pages/track/TrackMission'));
// PUBLIC legal pages required by the App Store / Google Play listings of the
// mobile apps — no auth, no layout, lazy for the same reason as TrackMission.
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const AccountDeletion = lazy(() => import('./pages/legal/AccountDeletion'));
// pages/requests only has named exports, so map each one to a default export.
const MyRequests = lazy(() => import('./pages/requests').then(m => ({ default: m.MyRequests })));
const PendingApprovals = lazy(() => import('./pages/requests').then(m => ({ default: m.PendingApprovals })));
const FinanceHub = lazy(() => import('./pages/finance/FinanceHub'));
const PeopleHub = lazy(() => import('./pages/people/PeopleHub'));
const EmployeesList = lazy(() => import('./pages/people/EmployeesList'));
const Employee = lazy(() => import('./pages/people/Employee'));
const HrSettings = lazy(() => import('./pages/people/HrSettings'));
const DocumentsBoard = lazy(() => import('./pages/people/DocumentsBoard'));
const RotaPlanner = lazy(() => import('./pages/people/RotaPlanner'));
const MyHr = lazy(() => import('./pages/people/MyHr'));
const PeopleDirectory = lazy(() => import('./pages/people/Directory'));
const LeavePage = lazy(() => import('./pages/people/LeavePage'));
const AttendanceBoardPage = lazy(() => import('./pages/people/AttendanceBoardPage'));
const ClaimsPage = lazy(() => import('./pages/people/ClaimsPage'));
const ClaimRecord = lazy(() => import('./pages/people/ClaimRecord'));
const PurchasesPage = lazy(() => import('./pages/people/PurchasesPage'));
const PurchaseRecord = lazy(() => import('./pages/people/PurchaseRecord'));
const AccessPage = lazy(() => import('./pages/people/AccessPage'));
const PayrollPage = lazy(() => import('./pages/people/PayrollPage'));
const PayrollRunRecord = lazy(() => import('./pages/people/PayrollRunRecord'));
const PayslipPage = lazy(() => import('./pages/people/PayslipPage'));
const LifecyclePage = lazy(() => import('./pages/people/LifecyclePage'));
const HrCasesPage = lazy(() => import('./pages/people/HrCasesPage'));
// Marketing
const MarketingTasks = lazy(() => import('./pages/marketing/MarketingTasks'));
const MarketingBoard = lazy(() => import('./pages/marketing/MarketingBoard'));
const MarketingBriefs = lazy(() => import('./pages/marketing/MarketingBriefs'));
const MarketingItem = lazy(() => import('./pages/marketing/MarketingItem'));
const MarketingSettings = lazy(() => import('./pages/marketing/MarketingSettings'));
const MissionSettings = lazy(() => import('./pages/mission/settings/MissionSettings'));
const MissionsMyWork = lazy(() => import('./pages/mission/myWork/MissionsMyWork'));
const MarketingReview = lazy(() => import('./pages/marketing/MarketingReview'));
// Business development
const BdHome = lazy(() => import('./pages/bd/BdHome'));
const BdOpportunities = lazy(() => import('./pages/bd/BdOpportunities'));
const BdOpportunityPage = lazy(() => import('./pages/bd/BdOpportunityPage'));
const BdNewAnalysis = lazy(() => import('./pages/bd/BdNewAnalysis'));
const BdDocumentPage = lazy(() => import('./pages/bd/BdDocumentPage'));
const BdDocumentsList = lazy(() => import('./pages/bd/BdWorkLists').then((m) => ({ default: m.BdDocumentsList })));
const BdProposalsList = lazy(() => import('./pages/bd/BdWorkLists').then((m) => ({ default: m.BdProposalsList })));
const BdNewProposal = lazy(() => import('./pages/bd/BdNewProposal'));
const BdProposalPage = lazy(() => import('./pages/bd/BdProposalPage'));
const BdSettingsPage = lazy(() => import('./pages/bd/BdSettingsPage'));
const BdAsksPage = lazy(() => import('./pages/bd/BdAskPages').then((m) => ({ default: m.BdAsksPage })));
const BdAskPage = lazy(() => import('./pages/bd/BdAskPages').then((m) => ({ default: m.BdAskPage })));
// Ask CRX and its pages are one conversation app (2026-11-02); old /bd/research links redirect.
const AskPage = lazy(() => import('./pages/ask/AskPage'));
const RedirectResearch = lazy(() => import('./pages/ask/RedirectResearch'));
const SignContract = lazy(() => import('./pages/sign/SignContract'));
// Training
const TrainingHome = lazy(() => import('./pages/training/TrainingHome'));
const TrainingJobs = lazy(() => import('./pages/training/TrainingJobs'));
const TrainingJobPage = lazy(() => import('./pages/training/TrainingJobPage'));
const TrainingCertificates = lazy(() => import('./pages/training/TrainingCertificates'));
const TrainingSettingsPage = lazy(() => import('./pages/training/TrainingSettingsPage'));
const TrainingLinkPage = lazy(() => import('./pages/training/public/TrainingLinkPage'));
const TrainingVerifyPage = lazy(() => import('./pages/training/public/TrainingVerifyPage'));
const TrainingMatrixPage = lazy(() => import('./pages/training/TrainingMatrixPage'));
const TrainingFacilitiesPage = lazy(() => import('./pages/training/TrainingFacilities'));
const CrmHub = lazy(() => import('./pages/crm/CrmHub'));
const MissionsHub = lazy(() => import('./pages/mission/MissionsHub'));
const RequestsHub = lazy(() => import('./pages/missionRequest/RequestsHub'));
const TasksHub = lazy(() => import('./pages/tasks/TasksHub'));
const InvoiceList = lazy(() => import('./pages/finance/InvoiceList'));
const InvoiceDetail = lazy(() => import('./pages/finance/InvoiceDetail'));
const BillList = lazy(() => import('./pages/finance/BillList'));
const BillDetail = lazy(() => import('./pages/finance/BillDetail'));
const PaymentList = lazy(() => import('./pages/finance/PaymentList'));
const PaymentDetail = lazy(() => import('./pages/finance/PaymentDetail'));
const Reports = lazy(() => import('./pages/finance/Reports'));
const ChartOfAccounts = lazy(() => import('./pages/finance/ChartOfAccounts'));
const PostingAccounts = lazy(() => import('./pages/finance/PostingAccounts'));
const AccountDetail = lazy(() => import('./pages/finance/AccountDetail'));
const CurrencyRates = lazy(() => import('./pages/finance/CurrencyRates'));
const AccountTypesList = lazy(() => import('./pages/lookups/accountType/AccountTypesList'));
const AccountTypeForm = lazy(() => import('./pages/lookups/accountType/AccountType'));
const FiscalPeriodsList = lazy(() => import('./pages/finance/FiscalPeriodsList'));
const FiscalPeriodDetail = lazy(() => import('./pages/finance/FiscalPeriodDetail'));
const JournalEntryList = lazy(() => import('./pages/finance/JournalEntryList'));
const JournalEntryDetail = lazy(() => import('./pages/finance/JournalEntryDetail'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const CreditNoteList = lazy(() => import('./pages/finance/CreditNoteList'));
const CreditNoteDetail = lazy(() => import('./pages/finance/CreditNoteDetail'));
const LeadsList = lazy(() => import('./pages/lead/LeadsList'));
const LeadDetail = lazy(() => import('./pages/lead/Lead'));
const LeadDashboard = lazy(() => import('./pages/lead/LeadDashboard'));
const TasksManagement = lazy(() => import('./pages/tasks/TasksManagement'));
const TaskDetail = lazy(() => import('./pages/tasks/TaskDetail'));
const TaskTemplateList = lazy(() => import('./pages/tasks/TaskTemplateList'));
const TaskTemplateDetail = lazy(() => import('./pages/tasks/TaskTemplateDetail'));
const MyProfile = lazy(() => import('./pages/profile/MyProfile'));
const ErrorLogs = lazy(() => import('./pages/system/ErrorLogs'));
const BugReports = lazy(() => import('./pages/system/BugReports'));
const BugReportDetail = lazy(() => import('./pages/system/BugReportDetail'));
const NotificationsInbox = lazy(() => import('./pages/notifications/NotificationsInbox'));
const Discussions = lazy(() => import('./pages/discussions/Discussions'));
const MyCalendar = lazy(() => import('./pages/calendar/MyCalendar'));
const SystemHealth = lazy(() => import('./pages/system/SystemHealth'));
const AiSettingsPage = lazy(() => import('./pages/system/AiSettingsPage'));
const ActiveSessions = lazy(() => import('./pages/system/ActiveSessions'));
const CacheManagement = lazy(() => import('./pages/system/CacheManagement'));
const EmailLogs = lazy(() => import('./pages/system/EmailLogs'));
const ScheduledJobs = lazy(() => import('./pages/system/ScheduledJobs'));
const LoginAudit = lazy(() => import('./pages/system/LoginAudit'));
const DiskUsage = lazy(() => import('./pages/system/DiskUsage'));
const DatabaseStats = lazy(() => import('./pages/system/DatabaseStats'));
const ErrorSummary = lazy(() => import('./pages/system/ErrorSummary'));
const StorageUsage = lazy(() => import('./pages/system/StorageUsage'));
const MigrationStatus = lazy(() => import('./pages/system/MigrationStatus'));
const LockedAccounts = lazy(() => import('./pages/system/LockedAccounts'));
const NotificationFilterMatrix = lazy(() => import('./pages/system/NotificationFilterMatrix'));
const NotificationEventTemplates = lazy(() => import('./pages/system/NotificationEventTemplates'));
const NotificationEventTemplateForm = lazy(() => import('./pages/system/NotificationEventTemplate'));
const FailedEmails = lazy(() => import('./pages/system/FailedEmails'));
const Backups = lazy(() => import('./pages/system/Backups'));
const AdminAudit = lazy(() => import('./pages/system/AdminAudit'));
const NotificationsAdmin = lazy(() => import('./pages/system/NotificationsAdmin'));
const QueueDashboard = lazy(() => import('./pages/system/QueueDashboard'));
const RatingDimensions = lazy(() => import('./pages/admin/RatingDimensions'));
// Standard Rates page hidden 2026-05-19 per product call — the in-line
// market-range hint on the Mission Request resource editor is the primary
// dispatcher surface for now; the dedicated catalog page will return when
// the Pricing Report module lands. Import + route + sidebar entry kept
// commented (not deleted) so unhiding is a one-line revert.
// const StandardRateCatalog = lazy(() => import('./pages/pricing/StandardRateCatalog'));
const CurrenciesList = lazy(() => import('./pages/lookups/currency/CurrenciesList'));
const CurrencyForm = lazy(() => import('./pages/lookups/currency/Currency'));
const EquipmentTypesList = lazy(() => import('./pages/lookups/equipmentType/EquipmentTypesList'));
const EquipmentTypeForm = lazy(() => import('./pages/lookups/equipmentType/EquipmentType'));
const ServiceCategoryTypesList = lazy(() => import('./pages/lookups/serviceCategoryType/ServiceCategoryTypesList'));
const ServiceCategoryTypeForm = lazy(() => import('./pages/lookups/serviceCategoryType/ServiceCategoryType'));
const LegTypesList = lazy(() => import('./pages/lookups/legType/LegTypesList'));
const LegTypeForm = lazy(() => import('./pages/lookups/legType/LegType'));
const CapabilitiesList = lazy(() => import('./pages/lookups/capability/CapabilitiesList'));
const CapabilityForm = lazy(() => import('./pages/lookups/capability/Capability'));
const CapabilityCategoryTypesList = lazy(() => import('./pages/lookups/capabilityCategoryType/CapabilityCategoryTypesList'));
const CapabilityCategoryTypeForm = lazy(() => import('./pages/lookups/capabilityCategoryType/CapabilityCategoryType'));

// Shown while a lazy page chunk is downloading — same bootstrap spinner the
// list pages use for their own loading states, centered in the content area.
const PageLoader = () => (
    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
        <span className="spinner-border" role="status" aria-label="Loading page" />
    </div>
);

const withNoLayout = (Component: FC<any>) => {
  return (
      <Component/>
  );
}


const withAuthLayout = (Component: FC<any>) => {
  return (
      <AuthLayout>
          <Component/>
      </AuthLayout>
  );
}

// Sets the browser tab title for the page being shown, and hands it to the
// persistent layout so the header breadcrumb can name pages with no nav entry.
const PageTitle: FC<{ title: string }> = ({ title }) => {
  const { setPageTitle } = useContext(PageTitleContext);
  useEffect(() => {
    if (title) {
      document.title = title;
    }
    setPageTitle(title);
  }, [title, setPageTitle]);
  return null;
};

// DefaultLayout is NOT rendered here: it is the parent layout route wrapping
// every private route below (see <Route element={<DefaultLayout />}>), so the
// header, sidebar and their notification socket stay mounted across navigation.
// Previously each route rendered its own DefaultLayout, and React only reused it
// when two routes happened to share the same wrapper shape — hopping between a
// guarded page and an unguarded one (Discussions, My Calendar, Search…) tore the
// whole shell down and rebuilt it.
// The boundary sits INSIDE the layout on purpose: when a page throws, the
// header and sidebar survive and the user can navigate away, instead of the
// whole app going blank. Every private page routes through here, so this one
// wrapper covers all of them.
// Suspense nests INSIDE the boundary so a failed chunk download (deploy raced
// a stale tab, network drop) surfaces in the per-route boundary too, with the
// layout intact.
const withDefaultLayout = (Component: ComponentType<any>, title: string = '') => {
  return (
      <>
          <PageTitle title={title} />
          <RouteErrorBoundary label={title}>
              <Suspense fallback={<PageLoader />}>
                  <Component/>
              </Suspense>
          </RouteErrorBoundary>
      </>
  );
}


function App() {
  return (
    <RealtimeProvider>
      <RealtimePanicOverlay />
      <RealtimeIncidentOverlay />
      <BuddyOverdueBanner />
      <Routes>
        {/* ------------------------------------public routes------------------------------------ */}
        <Route path="/login" element={<PublicOnlyRoute>{withAuthLayout(Login)}</PublicOnlyRoute>} />
        <Route path="/2fa" element={withAuthLayout(VerifyLoginForm)} />
        <Route
          path="/forgot-password"
          element={withAuthLayout(ForgotPassword)}
        />
        <Route
          path="/redeem-invite"
          element={withAuthLayout(RedeemInvite)}
        />
        {/* Public contract signing — standalone page, no app layout/auth; the token is the credential. */}
        <Route
          path="/sign/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <SignContract />
            </Suspense>
          }
        />
        {/* Public Training links (price form, trainee list, attendance and certificates) — standalone, no app layout/auth. */}
        <Route
          path="/t/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <TrainingLinkPage />
            </Suspense>
          }
        />
        {/* Public certificate check (the QR on a Crownox certificate) — standalone, no app layout/auth. */}
        <Route path="/v" element={<Suspense fallback={<PageLoader />}><TrainingVerifyPage /></Suspense>} />
        <Route path="/v/:code" element={<Suspense fallback={<PageLoader />}><TrainingVerifyPage /></Suspense>} />
        {/* Public Live Mission Link — standalone page, no app layout/auth. */}
        <Route
          path="/track/:token"
          element={
            <Suspense fallback={<PageLoader />}>
              <TrackMission />
            </Suspense>
          }
        />
        {/* Public legal pages (app-store requirement) — standalone, no app layout/auth. */}
        <Route
          path="/privacy"
          element={
            <Suspense fallback={<PageLoader />}>
              <PrivacyPolicy />
            </Suspense>
          }
        />
        <Route
          path="/account-deletion"
          element={
            <Suspense fallback={<PageLoader />}>
              <AccountDeletion />
            </Suspense>
          }
        />
        {/* ------------------------------------user routes------------------------------------ */}
        {/* One persistent layout route for every private page — the routes inside
            keep their original indentation to keep this change reviewable. */}
        <Route element={<DefaultLayout />}>
        <Route path="/" element={withDefaultLayout(Dashboard, PageName.DASHBOARD)} />
        <Route path="/my-profile" element={withDefaultLayout(MyProfile, "My Profile")} />
        {/* Your own notifications — no page permission, everyone has an inbox. */}
        <Route path="/notifications" element={withDefaultLayout(NotificationsInbox, "Notifications")} />
        {/* Your own discussions — server-scoped to the caller, so no page permission either. */}
        <Route path="/discussions" element={withDefaultLayout(Discussions, "Discussions")} />
        {/* Your own calendar — tasks / activities / follow-ups, server-scoped to the caller. */}
        <Route path="/my-calendar" element={withDefaultLayout(MyCalendar, "My Calendar")} />
        {/* Self-service: every signed-in user; the page explains when they aren't an employee. */}
        <Route path="/people/my" element={withDefaultLayout(MyHr, "My HR")} />
        {/* The people directory: every employee (the server refuses anyone else). */}
        <Route path="/people/directory" element={withDefaultLayout(PeopleDirectory, "People directory")} />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute roles={[
              PagesPermissions.DASHBOARD_READ,
              PagesPermissions.MISSIONS_DASHBOARD,
              PagesPermissions.MISSION_REQUESTS_DASHBOARD,
              PagesPermissions.LEADS_DASHBOARD,
              PagesPermissions.PROVIDERS_DASHBOARD,
              PagesPermissions.CLIENTS_DASHBOARD,
              PagesPermissions.COMPANIES_DASHBOARD,
              PagesPermissions.CONTACTS_DASHBOARD,
              PagesPermissions.TASKS_DASHBOARD,
            ]}>
              {withDefaultLayout(Dashboard, PageName.DASHBOARD)}
            </PrivateRoute>
          }
        />
        {/* ------------------------------------Report routes------------------------------------ */}
        <Route
          path="/reports"
          element={
            <PrivateRoute roles={[PagesPermissions.COMPANIES_REPORT, PagesPermissions.MISSIONS_REPORT, PagesPermissions.MISSION_REQUESTS_REPORT, PagesPermissions.LEADS_REPORT, PagesPermissions.PROVIDERS_REPORT, PagesPermissions.CLIENTS_REPORT, PagesPermissions.CONTACTS_REPORT, PagesPermissions.DASHBOARD_REPORT, PagesPermissions.INVOICES_REPORT, PagesPermissions.BILLS_REPORT, PagesPermissions.PAYMENTS_REPORT, PagesPermissions.CREDIT_NOTES_REPORT, PagesPermissions.JOURNAL_ENTRIES_REPORT, PagesPermissions.FINANCIAL_REPORTS_REPORT, PagesPermissions.CHART_OF_ACCOUNTS_REPORT, PagesPermissions.FISCAL_PERIODS_REPORT]}>
              {withDefaultLayout(ReportsPage, "Reports")}
            </PrivateRoute>
          }
        />
        {/* ------------------------------------Contact routes------------------------------------ */}
        <Route
          path="/contacts"
          element={
            <PrivateRoute roles={[PagesPermissions.CONTACTS_READ]}>
              {withDefaultLayout(ContactsList, PageName.CONTACTS)}
            </PrivateRoute>
          }
        />
<Route
          path="/contacts/merge"
          element={
            <PrivateRoute roles={[PagesPermissions.CONTACTS_EDIT]}>
              {withDefaultLayout(MergeContactsForm, PageName.CONTACTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/contact"
          element={
            <PrivateRoute roles={[PagesPermissions.CONTACTS_CREATE]}>
              {withDefaultLayout(Contact, PageName.CONTACTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/contact/:contactId"
          element={
            <PrivateRoute roles={[PagesPermissions.CONTACTS_EDIT,PagesPermissions.CONTACTS_READ]}>
              {withDefaultLayout(Contact, PageName.CONTACTS)}
            </PrivateRoute>
          }
        />
        {/* ------------------------------tasks route------------------------------------------ */}
        <Route
          path="/tasks"
          element={
            <PrivateRoute roles={[PagesPermissions.TASKS_READ]}>
              {withDefaultLayout(TasksManagement, PageName.TASKS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/tasks/new"
          element={
            <PrivateRoute roles={[PagesPermissions.TASKS_CREATE]}>
              {withDefaultLayout(TaskDetail, PageName.TASKS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/tasks/:id"
          element={
            <PrivateRoute roles={[PagesPermissions.TASKS_READ]}>
              {withDefaultLayout(TaskDetail, PageName.TASKS)}
            </PrivateRoute>
          }
        />
        {/* -------------------------task template routes--------------------------------------- */}
        <Route
          path="/task-templates"
          element={
            <PrivateRoute roles={[PagesPermissions.TASK_TEMPLATES_READ]}>
              {withDefaultLayout(TaskTemplateList, PageName.TASK_TEMPLATES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/task-templates/new"
          element={
            <PrivateRoute roles={[PagesPermissions.TASK_TEMPLATES_CREATE]}>
              {withDefaultLayout(TaskTemplateDetail, PageName.TASK_TEMPLATES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/task-templates/:id"
          element={
            <PrivateRoute roles={[PagesPermissions.TASK_TEMPLATES_READ]}>
              {withDefaultLayout(TaskTemplateDetail, PageName.TASK_TEMPLATES)}
            </PrivateRoute>
          }
        />
        {/* ------------------------------lead routes------------------------------------------ */}
        <Route
          path="/leads"
          element={
            <PrivateRoute roles={[PagesPermissions.LEADS_READ]}>
              {withDefaultLayout(LeadsList, PageName.LEADS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/lead/:leadId"
          element={
            <PrivateRoute roles={[PagesPermissions.LEADS_EDIT,PagesPermissions.LEADS_READ]}>
              {withDefaultLayout(LeadDetail, PageName.LEADS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/lead"
          element={
            <PrivateRoute roles={[PagesPermissions.LEADS_CREATE]}>
              {withDefaultLayout(LeadDetail, PageName.LEADS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/leads/dashboard"
          element={
            <PrivateRoute roles={[PagesPermissions.LEADS_READ]}>
              {withDefaultLayout(LeadDashboard, PageName.LEADS)}
            </PrivateRoute>
          }
        />
        {/* ------------------------------provider routes------------------------------------------ */}
        
        <Route
          path="/providers"
          element={
            <PrivateRoute roles={[PagesPermissions.PROVIDERS_READ]}>
              {withDefaultLayout(ProviderList, PageName.PROVIDERS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/provider/:providerId"
          element={
            <PrivateRoute roles={[PagesPermissions.PROVIDERS_EDIT,PagesPermissions.PROVIDERS_READ]}>
              {withDefaultLayout(Provider, PageName.PROVIDERS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/provider"
          element={
            <PrivateRoute roles={[PagesPermissions.PROVIDERS_CREATE]}>
              {withDefaultLayout(Provider, PageName.PROVIDERS)}
            </PrivateRoute>
          }
        />
<Route
          path="/providers/merge"
          element={
            <PrivateRoute roles={[PagesPermissions.PROVIDERS_EDIT]}>
              {withDefaultLayout(MergeOrganizationsForm, PageName.PROVIDERS)}
            </PrivateRoute>
          }
        />
        {/* ------------------------------client routes------------------------------------------ */}
        <Route
          path="/clients"
          element={
            <PrivateRoute roles={[PagesPermissions.CLIENTS_READ]}>
              {withDefaultLayout(ClientList, PageName.CLIENTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/client/:clientId"
          element={
            <PrivateRoute roles={[PagesPermissions.CLIENTS_EDIT,PagesPermissions.CLIENTS_READ]}>
              {withDefaultLayout(Client, PageName.CLIENTS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/client"
          element={
            <PrivateRoute roles={[PagesPermissions.CLIENTS_CREATE]}>
              {withDefaultLayout(Client, PageName.CLIENTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/clients/merge"
          element={
            <PrivateRoute roles={[PagesPermissions.CLIENTS_EDIT]}>
              {withDefaultLayout(MergeOrganizationsForm, PageName.CLIENTS)}
            </PrivateRoute>
          }
        />
 {/* ------------------------------company routes------------------------------------------ */}
 <Route
          path="/companies"
          element={
            <PrivateRoute roles={[PagesPermissions.COMPANIES_READ]}>
              {withDefaultLayout(CompaniesList, PageName.COMPANIES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/company/:organizationId"
          element={
            <PrivateRoute roles={[PagesPermissions.COMPANIES_EDIT,PagesPermissions.COMPANIES_READ]}>
              {withDefaultLayout(CompanyForm, PageName.COMPANIES)}
            </PrivateRoute>
          }
        />

        <Route
          path="/company"
          element={
            <PrivateRoute roles={[PagesPermissions.COMPANIES_CREATE]}>
              {withDefaultLayout(CompanyForm, PageName.COMPANIES)}
            </PrivateRoute>
          }
        />
<Route
          path="/companies/merge"
          element={
            <PrivateRoute roles={[PagesPermissions.COMPANIES_EDIT]}>
              {withDefaultLayout(MergeOrganizationsForm, PageName.COMPANIES)}
            </PrivateRoute>
          }
        />

        {/* ------------------------------super admin routes ------------------------------------------ */}
        {/* ------------------------------Roles routes ------------------------------------------ */}
        <Route
          path="/roles"
          element={
            <PrivateRoute roles={[PagesPermissions.ROLES_READ]}>
              {withDefaultLayout(RoleList, PageName.ROLES)}
            </PrivateRoute>
          }
        />

        <Route
          path="/role"
          element={
            <PrivateRoute roles={[PagesPermissions.ROLES_CREATE]}>
              {withDefaultLayout(Role, PageName.ROLES)}
            </PrivateRoute>
          }
        />

        <Route
          path="/role/:roleId"
          element={
            <PrivateRoute roles={[PagesPermissions.ROLES_EDIT,PagesPermissions.ROLES_READ]}>
              {withDefaultLayout(Role, PageName.ROLES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/crx"
          element={
            <PrivateRoute roles={[PagesPermissions.ORGANIZATION_EDIT]}>
              {withDefaultLayout(OrganizationDetails, "CRX")}
            </PrivateRoute>
          }
        />
        {/* ------------------------------Teams routes ------------------------------------------ */}
        <Route
          path="/teams"
          element={
            <PrivateRoute roles={[PagesPermissions.TEAMS_READ]}>
              {withDefaultLayout(TeamList, PageName.TEAMS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/team"
          element={
            <PrivateRoute roles={[PagesPermissions.TEAMS_CREATE]}>
              {withDefaultLayout(Team, PageName.TEAMS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/team/:teamId"
          element={
            <PrivateRoute roles={[PagesPermissions.TEAMS_EDIT,PagesPermissions.TEAMS_READ]}>
              {withDefaultLayout(Team, PageName.TEAMS)}
            </PrivateRoute>
          }
        />

        {/* ------------------------------Users routes ------------------------------------------ */}
        <Route
          path="/users"
          element={
            <PrivateRoute roles={[PagesPermissions.USERS_READ]}>
              {withDefaultLayout(UserList, PageName.USERS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/user"
          element={
            <PrivateRoute roles={[PagesPermissions.USERS_CREATE]}>
              {withDefaultLayout(UserDetails, PageName.USERS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/user/:userId"
          element={
            <PrivateRoute roles={[PagesPermissions.USERS_EDIT,PagesPermissions.USERS_READ]}>
              {withDefaultLayout(UserDetails, PageName.USERS)}
            </PrivateRoute>
          }
        />

        {/* Lookup Routes */}

        <Route
          path="/services"
          element={
            <PrivateRoute roles={[PagesPermissions.SERVICES_READ]}>
              {withDefaultLayout(ServiceList, PageName.SERVICES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/service/:serviceId"
          element={
            <PrivateRoute roles={[PagesPermissions.SERVICES_EDIT,PagesPermissions.SERVICES_READ]}>
              {withDefaultLayout(ServiceForm, PageName.SERVICES)}
            </PrivateRoute>
          }
        />

        <Route
          path="/service"
          element={
            <PrivateRoute roles={[PagesPermissions.SERVICES_CREATE]}>
              {withDefaultLayout(ServiceForm, PageName.SERVICES)}
            </PrivateRoute>
          }
        />

        <Route
          path="/service/:serviceId/sub-service/:subServiceId"
          element={
            <PrivateRoute roles={[PagesPermissions.SERVICES_EDIT,PagesPermissions.SERVICES_READ]}>
              {withDefaultLayout(SubServiceForm, "SubService")}
            </PrivateRoute>
          }
        />

        {/* Create route (no subServiceId) so the open-lookup "add a new
            sub-service" icon on sub-service pickers lands on an empty form. */}
        <Route
          path="/service/:serviceId/sub-service"
          element={
            <PrivateRoute roles={[PagesPermissions.SERVICES_CREATE]}>
              {withDefaultLayout(SubServiceForm, "SubService")}
            </PrivateRoute>
          }
        />

        <Route
          path="/tags"
          element={
            <PrivateRoute roles={[PagesPermissions.TAGS_READ]}>
              {withDefaultLayout(TagList, PageName.TAGS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/tag/:tagId"
          element={
            <PrivateRoute roles={[PagesPermissions.TAGS_EDIT,PagesPermissions.TAGS_READ]}>
              {withDefaultLayout(TagForm, PageName.TAGS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/tag"
          element={
            <PrivateRoute roles={[PagesPermissions.TAGS_CREATE]}>
              {withDefaultLayout(TagForm, PageName.TAGS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/manufacturers"
          element={
            <PrivateRoute roles={[PagesPermissions.MANUFACTURERS_READ]}>
              {withDefaultLayout(ManufacturerList, PageName.MANUFACTURERS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/manufacturer/:manufacturerId"
          element={
            <PrivateRoute roles={[PagesPermissions.MANUFACTURERS_EDIT,PagesPermissions.MANUFACTURERS_READ]}>
              {withDefaultLayout(ManufacturerForm, PageName.MANUFACTURERS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/manufacturer"
          element={
            <PrivateRoute roles={[PagesPermissions.MANUFACTURERS_CREATE]}>
              {withDefaultLayout(ManufacturerForm, PageName.MANUFACTURERS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/expenses"
          element={
            <PrivateRoute roles={[PagesPermissions.EXPENSES_READ]}>
              {withDefaultLayout(ExpenseList, PageName.EXPENSES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/expense/:expenseId"
          element={
            <PrivateRoute roles={[PagesPermissions.EXPENSES_EDIT,PagesPermissions.EXPENSES_READ]}>
              {withDefaultLayout(ExpenseForm, PageName.EXPENSES)}
            </PrivateRoute>
          }
        />

        <Route
          path="/expense"
          element={
            <PrivateRoute roles={[PagesPermissions.EXPENSES_CREATE]}>
              {withDefaultLayout(ExpenseForm, PageName.EXPENSES)}
            </PrivateRoute>
          }
        />

        <Route
          path="/incidents"
          element={
            <PrivateRoute roles={[PagesPermissions.INCIDENTS_READ]}>
              {withDefaultLayout(IncidentList, PageName.INCIDENTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/incident/:incidentId"
          element={
            <PrivateRoute roles={[PagesPermissions.INCIDENTS_EDIT,PagesPermissions.INCIDENTS_READ]}>
              {withDefaultLayout(IncidentForm, PageName.INCIDENTS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/incident"
          element={
            <PrivateRoute roles={[PagesPermissions.INCIDENTS_CREATE]}>
              {withDefaultLayout(IncidentForm, PageName.INCIDENTS)}
            </PrivateRoute>
          }
        />

<Route
          path="/languages"
          element={
            <PrivateRoute roles={[PagesPermissions.LANGUAGES_READ]}>
              {withDefaultLayout(LanguageList, PageName.LANGUAGES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/language/:languageId"
          element={
            <PrivateRoute roles={[PagesPermissions.LANGUAGES_EDIT,PagesPermissions.LANGUAGES_READ]}>
              {withDefaultLayout(LanguageForm, PageName.LANGUAGES)}
            </PrivateRoute>
          }
        />

        <Route
          path="/language"
          element={
            <PrivateRoute roles={[PagesPermissions.LANGUAGES_CREATE]}>
              {withDefaultLayout(LanguageForm, PageName.LANGUAGES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/colors"
          element={
            <PrivateRoute roles={[PagesPermissions.COLORS_READ]}>
              {withDefaultLayout(ColorList, PageName.COLORS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/color/:colorId"
          element={
            <PrivateRoute roles={[PagesPermissions.COLORS_EDIT,PagesPermissions.COLORS_READ]}>
              {withDefaultLayout(ColorForm,  PageName.COLORS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/color"
          element={
            <PrivateRoute roles={[PagesPermissions.COLORS_CREATE]}>
              {withDefaultLayout(ColorForm,  PageName.COLORS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/regions"
          element={
            <PrivateRoute roles={[PagesPermissions.REGIONS_READ]}>
              {withDefaultLayout(RegionList,  PageName.REGIONS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/region/:regionId"
          element={
            <PrivateRoute roles={[PagesPermissions.REGIONS_EDIT,PagesPermissions.REGIONS_READ]}>
              {withDefaultLayout(RegionForm, PageName.REGIONS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/region"
          element={
            <PrivateRoute roles={[PagesPermissions.REGIONS_CREATE]}>
              {withDefaultLayout(RegionForm, PageName.REGIONS)}
            </PrivateRoute>
          }
        />

<Route
          path="/continents"
          element={
            <PrivateRoute roles={[PagesPermissions.CONTINENT_READ]}>
              {withDefaultLayout(ContinentList,  PageName.CONTINENT)}
            </PrivateRoute>
          }
        />
        <Route
          path="/continent/:continentId"
          element={
            <PrivateRoute roles={[PagesPermissions.CONTINENT_EDIT,PagesPermissions.CONTINENT_READ]}>
              {withDefaultLayout(ContinentForm, PageName.CONTINENT)}
            </PrivateRoute>
          }
        />

        <Route
          path="/continent"
          element={
            <PrivateRoute roles={[PagesPermissions.CONTINENT_CREATE]}>
              {withDefaultLayout(ContinentForm, PageName.CONTINENT)}
            </PrivateRoute>
          }
        />

<Route
          path="/countries"
          element={
            <PrivateRoute roles={[PagesPermissions.COUNTRIES_READ]}>
              {withDefaultLayout(CountryList, PageName.COUNTRIES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/country/:countryId"
          element={
            <PrivateRoute roles={[PagesPermissions.COUNTRIES_EDIT,PagesPermissions.COUNTRIES_READ]}>
              {withDefaultLayout(CountryForm, PageName.COUNTRIES)}
            </PrivateRoute>
          }
        />

        <Route
          path="/country"
          element={
            <PrivateRoute roles={[PagesPermissions.COUNTRIES_CREATE]}>
              {withDefaultLayout(CountryForm, PageName.COUNTRIES)}
            </PrivateRoute>
          }
        />

<Route
          path="/states"
          element={
            <PrivateRoute roles={[PagesPermissions.STATES_READ]}>
              {withDefaultLayout(StateList, PageName.STATES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/state/:stateId"
          element={
            <PrivateRoute roles={[PagesPermissions.STATES_EDIT,PagesPermissions.STATES_READ]}>
              {withDefaultLayout(StateForm, PageName.STATES)}
            </PrivateRoute>
          }
        />

        <Route
          path="/state"
          element={
            <PrivateRoute roles={[PagesPermissions.STATES_CREATE]}>
              {withDefaultLayout(StateForm, PageName.STATES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/cities"
          element={
            <PrivateRoute roles={[PagesPermissions.CITIES_READ]}>
              {withDefaultLayout(CityList, PageName.CITIES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/city/:cityId"
          element={
            <PrivateRoute roles={[PagesPermissions.CITIES_EDIT,PagesPermissions.CITIES_READ]}>
              {withDefaultLayout(CityForm, PageName.CITIES)}
            </PrivateRoute>
          }
        />

        <Route
          path="/city"
          element={
            <PrivateRoute roles={[PagesPermissions.CITIES_CREATE]}>
              {withDefaultLayout(CityForm, PageName.CITIES)}
            </PrivateRoute>
          }
        />

<Route
          path="/timezones"
          element={
            <PrivateRoute roles={[PagesPermissions.TIMEZONES_READ]}>
              {withDefaultLayout(TimeZoneList, PageName.TIMEZONES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/timezone/:timeZoneId"
          element={
            <PrivateRoute roles={[PagesPermissions.TIMEZONES_EDIT,PagesPermissions.TIMEZONES_READ]}>
              {withDefaultLayout(TimeZoneForm, PageName.TIMEZONES)}
            </PrivateRoute>
          }
        />

        <Route
          path="/timezone"
          element={
            <PrivateRoute roles={[PagesPermissions.TIMEZONES_CREATE]}>
              {withDefaultLayout(TimeZoneForm, PageName.TIMEZONES)}
            </PrivateRoute>
          }
        />

<Route
          path="/airports"
          element={
            <PrivateRoute roles={[PagesPermissions.AIRPORTS_READ]}>
              {withDefaultLayout(AirportList, PageName.AIRPORTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/airport/:airportId"
          element={
            <PrivateRoute roles={[PagesPermissions.AIRPORTS_EDIT,PagesPermissions.AIRPORTS_READ]}>
              {withDefaultLayout(AirportForm, PageName.AIRPORTS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/airport"
          element={
            <PrivateRoute roles={[PagesPermissions.AIRPORTS_CREATE]}>
              {withDefaultLayout(AirportForm, PageName.AIRPORTS)}
            </PrivateRoute>
          }
        />

<Route
          path="/job-positions"
          element={
            <PrivateRoute roles={[PagesPermissions.JOB_POSITIONS_READ]}>
              {withDefaultLayout(JobPositionsList, PageName.JOB_POSITIONS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/job-position/:jobPositionId"
          element={
            <PrivateRoute roles={[PagesPermissions.JOB_POSITIONS_EDIT,PagesPermissions.JOB_POSITIONS_READ]}>
              {withDefaultLayout(JobPositionForm, PageName.JOB_POSITIONS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/job-position"
          element={
            <PrivateRoute roles={[PagesPermissions.JOB_POSITIONS_CREATE]}>
              {withDefaultLayout(JobPositionForm, PageName.JOB_POSITIONS)}
            </PrivateRoute>
          }
        />


<Route
          path="/search-aliases"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(SearchAliasesList, "Search Aliases")}
            </PrivateRoute>
          }
        />
        <Route
          path="/search-alias/:searchAliasId"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_EDIT, PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(SearchAliasForm, "Search Alias")}
            </PrivateRoute>
          }
        />
        <Route
          path="/search-alias"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_CREATE]}>
              {withDefaultLayout(SearchAliasForm, "Search Alias")}
            </PrivateRoute>
          }
        />
<Route
          path="/specialties"
          element={
            <PrivateRoute roles={[PagesPermissions.SPECIALTY_READ]}>
              {withDefaultLayout(OrganizationSpecialtyList, PageName.SPECIALTY)}
            </PrivateRoute>
          }
        />
        <Route
          path="/specialty/:specialtyId"
          element={
            <PrivateRoute roles={[PagesPermissions.SPECIALTY_EDIT,PagesPermissions.SPECIALTY_READ]}>
              {withDefaultLayout(OrganizationSpecialtyForm, PageName.SPECIALTY)}
            </PrivateRoute>
          }
        />

        <Route
          path="/specialty"
          element={
            <PrivateRoute roles={[PagesPermissions.SPECIALTY_CREATE]}>
              {withDefaultLayout(OrganizationSpecialtyForm, PageName.SPECIALTY)}
            </PrivateRoute>
          }
        />


<Route
          path="/mission-requests"
          element={
            <PrivateRoute roles={[PagesPermissions.MISSION_REQUESTS_READ]}>
              {withDefaultLayout(MissionRequestsList, PageName.MISSION_REQUESTS)}
            </PrivateRoute>
          }
        />
<Route
          path="/mission-request/:missionRequestId"
          element={
            <PrivateRoute roles={[PagesPermissions.MISSION_REQUESTS_EDIT,PagesPermissions.MISSION_REQUESTS_READ]}>
              {withDefaultLayout(MissionRequestDetails, PageName.MISSION_REQUESTS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/mission-request"
          element={
            <PrivateRoute roles={[PagesPermissions.MISSION_REQUESTS_CREATE]}>
              {withDefaultLayout(MissionRequestDetails, PageName.MISSION_REQUESTS)}
            </PrivateRoute>
          }
        />

<Route
          path="/missions"
          element={
            <PrivateRoute roles={[PagesPermissions.MISSIONS_READ]}>
              {withDefaultLayout(MissionsList, PageName.MISSIONS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/ops-board"
          element={
            <PrivateRoute roles={[PagesPermissions.MISSIONS_READ]}>
              {withDefaultLayout(OpsBoard, PageName.MISSIONS)}
            </PrivateRoute>
          }
        />
        <Route path="/mission-settings" element={<PrivateRoute roles={[PagesPermissions.MISSION_SETTINGS_READ]}>{withDefaultLayout(MissionSettings, PageName.MISSION_SETTINGS)}</PrivateRoute>} />
        <Route path="/missions/my-work" element={<PrivateRoute roles={[PagesPermissions.MISSIONS_READ, PagesPermissions.MISSION_REQUESTS_READ]}>{withDefaultLayout(MissionsMyWork, PageName.MISSIONS)}</PrivateRoute>} />
        <Route
          path="/procedures"
          element={
            <PrivateRoute roles={[PagesPermissions.MISSIONS_READ]}>
              {withDefaultLayout(ProceduresPage, PageName.MISSIONS)}
            </PrivateRoute>
          }
        />
        {/* Risk on our routes replaced the World incidents map (its data still feeds the risk checks) */}
        <Route path="/risk" element={<PrivateRoute roles={[PagesPermissions.MISSIONS_READ, PagesPermissions.MISSION_REQUESTS_READ]}>{withDefaultLayout(RiskPage, PageName.MISSIONS)}</PrivateRoute>} />
        <Route path="/world-incidents" element={<Navigate to="/risk" replace />} />
<Route
          path="/mission/:missionId"
          element={
            <PrivateRoute roles={[PagesPermissions.MISSIONS_EDIT,PagesPermissions.MISSIONS_READ]}>
              {withDefaultLayout(MissionDetails, PageName.MISSIONS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/mission"
          element={
            <PrivateRoute roles={[PagesPermissions.MISSIONS_CREATE]}>
              {withDefaultLayout(MissionDetails, PageName.MISSIONS)}
            </PrivateRoute>
          }
        />

        {/* ------------------------------Request routes ------------------------------------------ */}
        <Route
          path="/my-requests"
          element={
            <PrivateRoute roles={[PagesPermissions.MISSIONS_REQUEST]}>
              {withDefaultLayout(MyRequests, "Field Requests")}
            </PrivateRoute>
          }
        />
        <Route
          path="/pending-approvals"
          element={
            <PrivateRoute roles={[PagesPermissions.MISSIONS_APPROVE]}>
              {withDefaultLayout(PendingApprovals, "Field Approvals")}
            </PrivateRoute>
          }
        />
        {/* CRM home. Reachable with ANY CRM read permission (PrivateRoute's
            role list is OR), so a leads-only or contacts-only user still gets a
            landing page; the hub itself hides the blocks they can't see. */}
        <Route
          path="/crm"
          element={
            <PrivateRoute roles={[
              PagesPermissions.LEADS_READ,
              PagesPermissions.PROVIDERS_READ,
              PagesPermissions.CLIENTS_READ,
              PagesPermissions.COMPANIES_READ,
              PagesPermissions.CONTACTS_READ,
            ]}>
              {withDefaultLayout(CrmHub, "CRM")}
            </PrivateRoute>
          }
        />
        {/* People and HR. The server narrows every list and record to the
            user's reach on the Employees page (All, Office, Team, Own). */}
        <Route
          path="/people"
          element={
            <PrivateRoute roles={[PagesPermissions.EMPLOYEES_READ]}>
              {withDefaultLayout(PeopleHub, "People")}
            </PrivateRoute>
          }
        />
        <Route
          path="/people/employees"
          element={
            <PrivateRoute roles={[PagesPermissions.EMPLOYEES_READ]}>
              {withDefaultLayout(EmployeesList, PageName.EMPLOYEES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/people/employee/:employmentId"
          element={
            <PrivateRoute roles={[PagesPermissions.EMPLOYEES_READ]}>
              {withDefaultLayout(Employee, PageName.EMPLOYEES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/people/rota"
          element={
            <PrivateRoute roles={[PagesPermissions.ROTA_READ]}>
              {withDefaultLayout(RotaPlanner, PageName.ROTA)}
            </PrivateRoute>
          }
        />
        <Route
          path="/people/leave"
          element={withDefaultLayout(LeavePage, PageName.LEAVE)}
        />
        {/* Claims are self-service; the server decides who sees and approves each one. */}
        <Route path="/people/claims" element={withDefaultLayout(ClaimsPage, PageName.EXPENSE_CLAIMS)} />
        <Route path="/people/claim/:claimId" element={withDefaultLayout(ClaimRecord, PageName.EXPENSE_CLAIMS)} />
        {/* Purchases: approvers reach a purchase through its link even without the page; the server decides. */}
        <Route path="/people/purchases" element={withDefaultLayout(PurchasesPage, PageName.PURCHASES)} />
        <Route path="/people/purchase/:purchaseId" element={withDefaultLayout(PurchaseRecord, PageName.PURCHASES)} />
        {/* Access and audit: tabs by Employees:logs / Roles:read; renders nothing without either. */}
        <Route path="/people/access" element={withDefaultLayout(AccessPage, "Access and audit")} />
        {/* Payroll: advances and payslips are self-service; the server decides who sees each run and payslip. */}
        <Route path="/people/payroll" element={withDefaultLayout(PayrollPage, PageName.PAYROLL)} />
        <Route path="/people/payroll/:runId" element={withDefaultLayout(PayrollRunRecord, PageName.PAYROLL)} />
        <Route path="/people/payslip/:payslipId" element={withDefaultLayout(PayslipPage, "Payslip")} />
        <Route path="/people/lifecycle" element={withDefaultLayout(LifecyclePage, PageName.LIFECYCLE)} />
        {/* Confidential: renders nothing without HR cases access and the performance data class. */}
        <Route path="/people/cases" element={withDefaultLayout(HrCasesPage, PageName.HR_CASES)} />
        {/* Marketing: brief → writing → text approval → design → design approval → scheduling → published */}
        <Route path="/marketing" element={<PrivateRoute roles={[PagesPermissions.MARKETING_READ]}>{withDefaultLayout(MarketingTasks, "Marketing")}</PrivateRoute>} />
        <Route path="/marketing/board" element={<PrivateRoute roles={[PagesPermissions.MARKETING_READ]}>{withDefaultLayout(MarketingBoard, "Content board")}</PrivateRoute>} />
        <Route path="/marketing/briefs" element={<PrivateRoute roles={[PagesPermissions.MARKETING_READ]}>{withDefaultLayout(MarketingBriefs, "Briefs")}</PrivateRoute>} />
        <Route path="/marketing/items/:id" element={<PrivateRoute roles={[PagesPermissions.MARKETING_READ]}>{withDefaultLayout(MarketingItem, "Marketing item")}</PrivateRoute>} />
        <Route path="/marketing/review" element={<PrivateRoute roles={[PagesPermissions.MARKETING_REVIEW_READ]}>{withDefaultLayout(MarketingReview, PageName.MARKETING_REVIEW)}</PrivateRoute>} />
        <Route path="/marketing/settings" element={<PrivateRoute roles={[PagesPermissions.MARKETING_SETTINGS_READ]}>{withDefaultLayout(MarketingSettings, PageName.MARKETING_SETTINGS)}</PrivateRoute>} />
        {/* Business development: analyse → share back → their reply; proposals; opportunities. Questions to colleagues need no BD access. */}
        <Route path="/bd" element={<PrivateRoute roles={[PagesPermissions.BUSINESS_DEVELOPMENT_READ]}>{withDefaultLayout(BdHome, "My work")}</PrivateRoute>} />
        <Route path="/bd/opportunities" element={<PrivateRoute roles={[PagesPermissions.BUSINESS_DEVELOPMENT_READ]}>{withDefaultLayout(BdOpportunities, "Opportunities")}</PrivateRoute>} />
        <Route path="/bd/opportunities/:id" element={<PrivateRoute roles={[PagesPermissions.BUSINESS_DEVELOPMENT_READ]}>{withDefaultLayout(BdOpportunityPage, "Opportunity")}</PrivateRoute>} />
        <Route path="/bd/analyse" element={<PrivateRoute roles={[PagesPermissions.BUSINESS_DEVELOPMENT_CREATE]}>{withDefaultLayout(BdNewAnalysis, "Analyse a document")}</PrivateRoute>} />
        <Route path="/bd/documents" element={<PrivateRoute roles={[PagesPermissions.BUSINESS_DEVELOPMENT_READ]}>{withDefaultLayout(BdDocumentsList, "Analyses")}</PrivateRoute>} />
        <Route path="/bd/documents/:id" element={<PrivateRoute roles={[PagesPermissions.BUSINESS_DEVELOPMENT_READ]}>{withDefaultLayout(BdDocumentPage, "Analysis")}</PrivateRoute>} />
        <Route path="/bd/proposals" element={<PrivateRoute roles={[PagesPermissions.BUSINESS_DEVELOPMENT_READ]}>{withDefaultLayout(BdProposalsList, "Proposals")}</PrivateRoute>} />
        <Route path="/bd/proposals/new" element={<PrivateRoute roles={[PagesPermissions.BUSINESS_DEVELOPMENT_CREATE]}>{withDefaultLayout(BdNewProposal, "Write a proposal")}</PrivateRoute>} />
        <Route path="/bd/proposals/:id" element={<PrivateRoute roles={[PagesPermissions.BUSINESS_DEVELOPMENT_READ]}>{withDefaultLayout(BdProposalPage, "Proposal")}</PrivateRoute>} />
        <Route path="/bd/settings" element={<PrivateRoute roles={[PagesPermissions.BD_SETTINGS_READ, PagesPermissions.BUSINESS_DEVELOPMENT_READ]}>{withDefaultLayout(BdSettingsPage, PageName.BD_SETTINGS)}</PrivateRoute>} />
        <Route path="/bd/asks" element={withDefaultLayout(BdAsksPage, "Questions for me")} />
        <Route path="/bd/asks/:id" element={withDefaultLayout(BdAskPage, "Question")} />
        {/* Ask CRX — every signed-in user; the page itself says when AI is off for them. */}
        <Route path="/ask" element={withDefaultLayout(AskPage, "Ask CRX")} />
        <Route path="/ask/:chatId" element={withDefaultLayout(AskPage, "Ask CRX")} />
        <Route path="/bd/research" element={<Navigate to="/ask" replace />} />
        <Route path="/bd/research/new" element={<Navigate to="/ask" replace />} />
        {/* A page's conversation when it has one; the old page otherwise (the server checks who may read it). */}
        <Route path="/bd/research/:id" element={withDefaultLayout(RedirectResearch, "Ask CRX")} />
        {/* Training */}
        <Route path="/training" element={<PrivateRoute roles={[PagesPermissions.TRAINING_READ]}>{withDefaultLayout(TrainingHome, "Training home")}</PrivateRoute>} />
        <Route path="/training/jobs" element={<PrivateRoute roles={[PagesPermissions.TRAINING_READ]}>{withDefaultLayout(TrainingJobs, "Training jobs")}</PrivateRoute>} />
        <Route path="/training/jobs/:id" element={<PrivateRoute roles={[PagesPermissions.TRAINING_READ]}>{withDefaultLayout(TrainingJobPage, "Training job")}</PrivateRoute>} />
        <Route path="/training/certificates" element={<PrivateRoute roles={[PagesPermissions.TRAINING_READ]}>{withDefaultLayout(TrainingCertificates, "Certificates")}</PrivateRoute>} />
        <Route path="/training/matrix" element={<PrivateRoute roles={[PagesPermissions.TRAINING_READ]}>{withDefaultLayout(TrainingMatrixPage, "Training matrix")}</PrivateRoute>} />
        <Route path="/training/facilities" element={<PrivateRoute roles={[PagesPermissions.TRAINING_READ, PagesPermissions.TRAINING_SETTINGS_READ]}>{withDefaultLayout(TrainingFacilitiesPage, "Training facilities")}</PrivateRoute>} />
        <Route path="/training/settings" element={<PrivateRoute roles={[PagesPermissions.TRAINING_SETTINGS_READ, PagesPermissions.TRAINING_READ]}>{withDefaultLayout(TrainingSettingsPage, PageName.TRAINING_SETTINGS)}</PrivateRoute>} />
        <Route
          path="/people/attendance"
          element={
            <PrivateRoute roles={[PagesPermissions.ATTENDANCE_READ]}>
              {withDefaultLayout(AttendanceBoardPage, PageName.ATTENDANCE)}
            </PrivateRoute>
          }
        />
        <Route
          path="/people/documents"
          element={
            <PrivateRoute roles={[PagesPermissions.EMPLOYEES_READ]}>
              {withDefaultLayout(DocumentsBoard, "Documents")}
            </PrivateRoute>
          }
        />
        <Route
          path="/hr-settings"
          element={
            <PrivateRoute roles={[PagesPermissions.HR_SETTINGS_READ]}>
              {withDefaultLayout(HrSettings, PageName.HR_SETTINGS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance"
          element={
            <PrivateRoute roles={[PagesPermissions.INVOICES_READ]}>
              {withDefaultLayout(FinanceHub, PageName.INVOICES)}
            </PrivateRoute>
          }
        />
        {/* Module hubs (2026-08-04): the former dashboard Missions/MR/Tasks
            tabs, now sidebar landing pages. Gated on the module's dashboard
            action — every widget inside requires it. */}
        <Route
          path="/missions-hub"
          element={
            <PrivateRoute roles={[PagesPermissions.MISSIONS_DASHBOARD]}>
              {withDefaultLayout(MissionsHub, PageName.MISSIONS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/requests-hub"
          element={
            <PrivateRoute roles={[PagesPermissions.MISSION_REQUESTS_DASHBOARD]}>
              {withDefaultLayout(RequestsHub, PageName.MISSION_REQUESTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/tasks-hub"
          element={
            <PrivateRoute roles={[PagesPermissions.TASKS_DASHBOARD]}>
              {withDefaultLayout(TasksHub, PageName.TASKS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/invoices"
          element={
            <PrivateRoute roles={[PagesPermissions.INVOICES_READ]}>
              {withDefaultLayout(InvoiceList, PageName.INVOICES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/invoice/:invoiceId"
          element={
            <PrivateRoute roles={[PagesPermissions.INVOICES_READ, PagesPermissions.INVOICES_EDIT]}>
              {withDefaultLayout(InvoiceDetail, PageName.INVOICES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/invoice/new"
          element={
            <PrivateRoute roles={[PagesPermissions.INVOICES_CREATE]}>
              {withDefaultLayout(InvoiceDetail, PageName.INVOICES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/bills"
          element={
            <PrivateRoute roles={[PagesPermissions.BILLS_READ]}>
              {withDefaultLayout(BillList, PageName.BILLS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/bill/:billId"
          element={
            <PrivateRoute roles={[PagesPermissions.BILLS_READ, PagesPermissions.BILLS_EDIT]}>
              {withDefaultLayout(BillDetail, PageName.BILLS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/bill/new"
          element={
            <PrivateRoute roles={[PagesPermissions.BILLS_CREATE]}>
              {withDefaultLayout(BillDetail, PageName.BILLS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/payments"
          element={
            <PrivateRoute roles={[PagesPermissions.PAYMENTS_READ]}>
              {withDefaultLayout(PaymentList, PageName.PAYMENTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/payment/:paymentId"
          element={
            <PrivateRoute roles={[PagesPermissions.PAYMENTS_READ, PagesPermissions.PAYMENTS_EDIT]}>
              {withDefaultLayout(PaymentDetail, PageName.PAYMENTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/payment/new"
          element={
            <PrivateRoute roles={[PagesPermissions.PAYMENTS_CREATE]}>
              {withDefaultLayout(PaymentDetail, PageName.PAYMENTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/credit-notes"
          element={
            <PrivateRoute roles={[PagesPermissions.CREDIT_NOTES_READ]}>
              {withDefaultLayout(CreditNoteList, PageName.CREDIT_NOTES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/credit-note/:creditNoteId"
          element={
            <PrivateRoute roles={[PagesPermissions.CREDIT_NOTES_READ, PagesPermissions.CREDIT_NOTES_EDIT]}>
              {withDefaultLayout(CreditNoteDetail, PageName.CREDIT_NOTES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/credit-note/new"
          element={
            <PrivateRoute roles={[PagesPermissions.CREDIT_NOTES_CREATE]}>
              {withDefaultLayout(CreditNoteDetail, PageName.CREDIT_NOTES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/reports"
          element={
            <PrivateRoute roles={[PagesPermissions.FINANCIAL_REPORTS_READ]}>
              {withDefaultLayout(Reports, PageName.FINANCIAL_REPORTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/posting-accounts"
          element={
            <PrivateRoute roles={[PagesPermissions.CHART_OF_ACCOUNTS_READ]}>
              {withDefaultLayout(PostingAccounts, "Posting accounts")}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/chart-of-accounts"
          element={
            <PrivateRoute roles={[PagesPermissions.CHART_OF_ACCOUNTS_READ]}>
              {withDefaultLayout(ChartOfAccounts, PageName.CHART_OF_ACCOUNTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/currency-rates"
          element={
            <PrivateRoute roles={[PagesPermissions.INVOICES_CREATE]}>
              {withDefaultLayout(CurrencyRates, "Currency rates")}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/account"
          element={
            <PrivateRoute roles={[PagesPermissions.CHART_OF_ACCOUNTS_CREATE]}>
              {withDefaultLayout(AccountDetail, PageName.CHART_OF_ACCOUNTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/account/:accountId"
          element={
            <PrivateRoute roles={[PagesPermissions.CHART_OF_ACCOUNTS_READ, PagesPermissions.CHART_OF_ACCOUNTS_EDIT]}>
              {withDefaultLayout(AccountDetail, PageName.CHART_OF_ACCOUNTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/account-types"
          element={
            <PrivateRoute roles={[PagesPermissions.CHART_OF_ACCOUNTS_READ]}>
              {withDefaultLayout(AccountTypesList, PageName.CHART_OF_ACCOUNTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/account-type/:accountTypeId"
          element={
            <PrivateRoute roles={[PagesPermissions.CHART_OF_ACCOUNTS_EDIT, PagesPermissions.CHART_OF_ACCOUNTS_READ]}>
              {withDefaultLayout(AccountTypeForm, PageName.CHART_OF_ACCOUNTS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/account-type"
          element={
            <PrivateRoute roles={[PagesPermissions.CHART_OF_ACCOUNTS_CREATE]}>
              {withDefaultLayout(AccountTypeForm, PageName.CHART_OF_ACCOUNTS)}
            </PrivateRoute>
          }
        />

        <Route
          path="/finance/fiscal-periods"
          element={
            <PrivateRoute roles={[PagesPermissions.FISCAL_PERIODS_READ]}>
              {withDefaultLayout(FiscalPeriodsList, PageName.FISCAL_PERIODS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/fiscal-period/new"
          element={
            <PrivateRoute roles={[PagesPermissions.FISCAL_PERIODS_CREATE]}>
              {withDefaultLayout(FiscalPeriodDetail, PageName.FISCAL_PERIODS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/fiscal-period/:periodId"
          element={
            <PrivateRoute roles={[PagesPermissions.FISCAL_PERIODS_READ, PagesPermissions.FISCAL_PERIODS_EDIT]}>
              {withDefaultLayout(FiscalPeriodDetail, PageName.FISCAL_PERIODS)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/journal-entries"
          element={
            <PrivateRoute roles={[PagesPermissions.JOURNAL_ENTRIES_READ]}>
              {withDefaultLayout(JournalEntryList, PageName.JOURNAL_ENTRIES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/journal-entry/new"
          element={
            <PrivateRoute roles={[PagesPermissions.JOURNAL_ENTRIES_CREATE]}>
              {withDefaultLayout(JournalEntryDetail, PageName.JOURNAL_ENTRIES)}
            </PrivateRoute>
          }
        />
        <Route
          path="/finance/journal-entry/:journalEntryId"
          element={
            <PrivateRoute roles={[PagesPermissions.JOURNAL_ENTRIES_READ]}>
              {withDefaultLayout(JournalEntryDetail, PageName.JOURNAL_ENTRIES)}
            </PrivateRoute>
          }
        />


        {/* ------------------------------System Admin routes ------------------------------------------ */}
        <Route
          path="/system/error-logs"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(ErrorLogs, "Error Logs")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/search-log"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(SearchLog, "Search Log")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/bug-reports"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(BugReports, "Bug Reports")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/bug-reports/:id"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(BugReportDetail, "Bug Report")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/ai"
          element={
            <PrivateRoute roles={[PagesPermissions.AI_SETTINGS_READ]}>
              {withDefaultLayout(AiSettingsPage, "AI settings")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/health"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(SystemHealth, "System Health")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/sessions"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(ActiveSessions, "Active Sessions")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/cache"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(CacheManagement, "Cache Management")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/email-logs"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(EmailLogs, "Email Logs")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/jobs"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(ScheduledJobs, "Scheduled Jobs")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/login-audit"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(LoginAudit, "Login Audit")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/database"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(DatabaseStats, "Database Stats")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/disk"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(DiskUsage, "Disk Usage")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/error-summary"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(ErrorSummary, "Error Summary")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/storage"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(StorageUsage, "S3 Storage")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/migrations"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(MigrationStatus, "Migrations")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/locked-accounts"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(LockedAccounts, "Locked Accounts")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/failed-emails"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(FailedEmails, "Failed Emails")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/backups"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(Backups, "Backups")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/admin-audit"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(AdminAudit, "Admin Audit")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/notifications"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(NotificationsAdmin, "Notifications History")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/notification-filter-matrix"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(NotificationFilterMatrix, "Notification Filter Matrix")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/notification-event-templates"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(NotificationEventTemplates, "Notification Event Templates")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/notification-event-template"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(NotificationEventTemplateForm, "Notification Event Template")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/notification-event-template/:templateId"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(NotificationEventTemplateForm, "Notification Event Template")}
            </PrivateRoute>
          }
        />
        <Route
          path="/system/queues"
          element={
            <PrivateRoute roles={[PagesPermissions.CONFIGS_READ]}>
              {withDefaultLayout(QueueDashboard, "Queue Dashboard")}
            </PrivateRoute>
          }
        />

        <Route
          path="/rating-dimensions"
          element={
            <PrivateRoute roles={[PagesPermissions.RATING_DIMENSIONS_READ]}>
              {withDefaultLayout(RatingDimensions, PageName.RATING_DIMENSIONS)}
            </PrivateRoute>
          }
        />
        {/* ------------------------------Currency lookup routes ------------------------------------------ */}
        <Route
          path="/currencies"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(CurrenciesList, "Currencies")}
            </PrivateRoute>
          }
        />
        <Route
          path="/currency/:currencyId"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(CurrencyForm, "Currencies")}
            </PrivateRoute>
          }
        />
        <Route
          path="/currency"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_CREATE]}>
              {withDefaultLayout(CurrencyForm, "Currencies")}
            </PrivateRoute>
          }
        />
        {/* ------------------------------Equipment Type lookup routes ------------------------------------------ */}
        <Route
          path="/equipment-types"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(EquipmentTypesList, "Equipment Types")}
            </PrivateRoute>
          }
        />
        <Route
          path="/equipment-type/:equipmentTypeId"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(EquipmentTypeForm, "Equipment Types")}
            </PrivateRoute>
          }
        />
        <Route
          path="/equipment-type"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_CREATE]}>
              {withDefaultLayout(EquipmentTypeForm, "Equipment Types")}
            </PrivateRoute>
          }
        />
        {/* ------------------------------Leg Type lookup routes ------------------------------------------ */}
        <Route
          path="/leg-types"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(LegTypesList, "Leg Types")}
            </PrivateRoute>
          }
        />
        <Route
          path="/leg-type/:legTypeId"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(LegTypeForm, "Leg Types")}
            </PrivateRoute>
          }
        />
        <Route
          path="/leg-type"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_CREATE]}>
              {withDefaultLayout(LegTypeForm, "Leg Types")}
            </PrivateRoute>
          }
        />
        {/* ------------------------------Service Category Type lookup routes ------------------------------------------ */}
        <Route
          path="/service-category-types"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(ServiceCategoryTypesList, "Service Category Types")}
            </PrivateRoute>
          }
        />
        <Route
          path="/service-category-type/:serviceCategoryTypeId"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(ServiceCategoryTypeForm, "Service Category Types")}
            </PrivateRoute>
          }
        />
        <Route
          path="/service-category-type"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_CREATE]}>
              {withDefaultLayout(ServiceCategoryTypeForm, "Service Category Types")}
            </PrivateRoute>
          }
        />
        {/* ------------------------------Capability Category Type lookup routes ------------------------------------------ */}
        <Route
          path="/capability-category-types"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(CapabilityCategoryTypesList, "Capability Category Types")}
            </PrivateRoute>
          }
        />
        <Route
          path="/capability-category-type/:capabilityCategoryTypeId"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(CapabilityCategoryTypeForm, "Capability Category Types")}
            </PrivateRoute>
          }
        />
        <Route
          path="/capability-category-type"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_CREATE]}>
              {withDefaultLayout(CapabilityCategoryTypeForm, "Capability Category Types")}
            </PrivateRoute>
          }
        />
        {/* ------------------------------Capability lookup routes ------------------------------------------ */}
        <Route
          path="/capabilities"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(CapabilitiesList, "Capabilities")}
            </PrivateRoute>
          }
        />
        <Route
          path="/capability/:capabilityId"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_READ]}>
              {withDefaultLayout(CapabilityForm, "Capabilities")}
            </PrivateRoute>
          }
        />
        <Route
          path="/capability"
          element={
            <PrivateRoute roles={[PagesPermissions.lOOKUPS_CREATE]}>
              {withDefaultLayout(CapabilityForm, "Capabilities")}
            </PrivateRoute>
          }
        />
        {/* ------------------------------Pricing routes ------------------------------------------ */}
        {/* Standard Rates route hidden 2026-05-19 — restore alongside the
            sidebar entry + import at the top of this file when re-enabling.
            Direct URL access now falls through to the * NotFound route. */}
        {/* <Route
          path="/pricing/standard-rates"
          element={
            <PrivateRoute roles={[PagesPermissions.PROVIDERS_READ]}>
              {withDefaultLayout(StandardRateCatalog, "Standard Rates")}
            </PrivateRoute>
          }
        /> */}
        <Route path="/search" element={withDefaultLayout(SearchResults, "Search Results")} />
        <Route path="/denied" element={withDefaultLayout(AccessDenied)} />
        <Route path="/task/:id" element={<RedirectWithId to="/tasks/" />} />
        <Route path="/task-template/:id" element={<RedirectWithId to="/task-templates/" />} />
        <Route path="*" element={withDefaultLayout(NotFound)} />
        </Route>
      </Routes>
    </RealtimeProvider>
  );
}

export default App;
